import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAccount, useReadContract } from 'wagmi'
import { isAddress } from 'viem'
import { T } from '@/lib/theme'
import { Button, Panel } from '@/components/ui'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import registryAbi from '@/lib/abis/ReferralRegistry.json'
import { truncAddr } from '@/lib/utils'
import { useContractTx } from '@/hooks/useContractTx'

const REFERRAL_NOT_DEPLOYED = !IS_LIVE || (ADDRESSES.REFERRAL_REGISTRY || '').toLowerCase() === ZERO_ADDR.toLowerCase()

/// Captures a `?ref=0xAddress` link the first time a wallet connects with no referrer already
/// set - shows a dismissible prompt to bind it via `ReferralRegistry.setReferrer`, never signs
/// anything automatically (a wallet transaction is always an explicit click, never a page-load
/// side effect). Renders nothing once dismissed for this session, already bound, or if there's
/// no `ref` param/connected wallet/deployed registry to act on. Rendered once, in `AppShell`
/// (App.jsx), so a `ref` link works no matter which page it lands on.
export function ReferralCapture() {
  const [searchParams] = useSearchParams()
  const refParam = searchParams.get('ref')
  const { address: user } = useAccount()
  const tx = useContractTx()
  const [dismissed, setDismissed] = useState(false)

  const validRef = !!refParam && isAddress(refParam) && (!user || refParam.toLowerCase() !== user.toLowerCase())
  const enabled = !REFERRAL_NOT_DEPLOYED && !!user && validRef

  const { data: existingReferrer, refetch } = useReadContract({
    address: ADDRESSES.REFERRAL_REGISTRY, abi: registryAbi, functionName: 'referrerOf', args: [user],
    query: { enabled },
  })

  const shouldPrompt = enabled && !dismissed && existingReferrer === ZERO_ADDR

  async function bind() {
    await tx.run(
      { address: ADDRESSES.REFERRAL_REGISTRY, abi: registryAbi, functionName: 'setReferrer', args: [refParam] },
      { successMsg: 'Referral confirmed.' },
    )
    refetch().catch((e) => console.error('refetch referrerOf after bind failed:', e))
  }

  if (!shouldPrompt) return null

  return (
    <div style={{ maxWidth: 720, margin: '16px auto 0', padding: '0 16px' }}>
      <Panel style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <span style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          <span className="mono" style={{ color: T.text, fontWeight: 700 }}>{truncAddr(refParam)}</span>
          {' '}referred you to Horizon Protocol - confirm to link your activity to their referral.
        </span>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Button onClick={bind} disabled={tx.pending} style={{ padding: '8px 16px', fontSize: T.fs.caption }}>
            {tx.pending ? 'Working…' : 'Confirm referral'}
          </Button>
          <Button variant="ghost" onClick={() => setDismissed(true)} style={{ padding: '8px 16px', fontSize: T.fs.caption }}>
            Dismiss
          </Button>
        </div>
      </Panel>
    </div>
  )
}
